/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useRef, useState } from 'react';
import {
  useForm,
  FieldValues,
  SubmitHandler,
  Controller
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Form, Row, Col } from 'react-bootstrap';

import Button from 'components/base/Button';

interface FillInInvoiceItemProps {
  selectedRow: any;
  setOpenFillIn: (show: boolean) => void;
  productInvoiceData: any;
  setProductInvoiceData: any;
}

const FillInInvoiceItem = ({
  selectedRow,
  setOpenFillIn,
  productInvoiceData,
  setProductInvoiceData
}: FillInInvoiceItemProps) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement | any>(null);

  const {
    handleSubmit,
    control,
    reset,
    watch,
    formState: { errors }
  } = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange'
  });

  const [loading, setLoading] = useState(false);

  const formValues = watch();

  const validateDuplicate = (value: string, index: number) => {
    if (!value) return true;

    const allSerials = Object.entries(formValues)
      .filter(([key]) => key.startsWith('name_'))
      .map(([, val]) => val as string);

    const isDuplicateInForm =
      allSerials.filter((serial, idx) => serial === value && idx !== index)
        .length > 0;

    const isInExistingSerials = !!selectedRow?.partion_codes_map?.[value];

    if (isDuplicateInForm) return t('duplicate_serial_number');
    if (isInExistingSerials) return t('serial_number_already_exists');

    return true;
  };

  //Get by id
  const fetchData = () => {
    setLoading(true);
    if (
      !selectedRow?.serial_number ||
      !Array.isArray(selectedRow.serial_number)
    ) {
      setLoading(false);
      return;
    }

    const nameObj: any = {};
    selectedRow.serial_number.forEach((value: string, index: number) => {
      nameObj[`name_${index}`] = value;
    });

    reset(nameObj);
    setLoading(false);
  };

  const onSubmit: SubmitHandler<FieldValues> = data => {
    setLoading(true);

    // Extract serial numbers in correct order
    const serialNumbers: string[] = Object.entries(data)
      .filter(([key]) => key.startsWith('name_'))
      .sort(([a], [b]) => {
        const numA = parseInt(a.split('_')[1], 10);
        const numB = parseInt(b.split('_')[1], 10);
        return numA - numB;
      })
      .map(([, value]) => value as string);

    // Update only matching rows by barcode_original
    const updatedData = productInvoiceData?.map((item: any) => {
      if (item?.barcode_original === selectedRow?.barcode_original) {
        return {
          ...item,
          sale_with_partion_code: serialNumbers?.length > 0 ? true : false,
          serial_number: serialNumbers
        };
      }
      return item;
    });

    setProductInvoiceData(updatedData);
    setOpenFillIn(false);
  };

  useEffect(() => {
    if (selectedRow?.serial_number?.length > 0) {
      fetchData();
    }
  }, [selectedRow]);

  useEffect(() => {
    if (inputRef?.current && selectedRow?.quantity) {
      inputRef?.current?.focus();
      setTimeout(() => {
        inputRef?.current?.select();
      }, 500);
    }
  }, [selectedRow?.quantity]);

  return (
    <Form
      noValidate
      onSubmit={handleSubmit(onSubmit)}
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%'
      }}
    >
      {selectedRow?.quantity &&
        Array.from({ length: +selectedRow?.quantity }).map((_, index) => (
          <Row key={index} className="mb-3">
            <Col>
              <Controller
                name={`name_${index}`}
                control={control}
                rules={{
                  required: t('required_field'),
                  validate: value => validateDuplicate(value, index)
                }}
                render={({ field }) => (
                  <Form.Floating className="md-4">
                    <Form.Control
                      {...field}
                      ref={index === 0 ? inputRef : null}
                      type="text"
                      placeholder={`${t('serial_number')} ${index}`}
                      onFocus={e => e.target.select()}
                      isInvalid={!!errors[`name_${index}`]}
                    />
                    <label htmlFor={`name_${index}`}>
                      {t('serial_number')} ( {index + 1} )
                    </label>
                    {errors[`name_${index}`] && (
                      <Form.Control.Feedback type="invalid">
                        {errors[`name_${index}`]?.type === 'required'
                          ? t('required_field')
                          : t(errors[`name_${index}`]?.message as string) ||
                            t('invalid_input')}
                      </Form.Control.Feedback>
                    )}
                  </Form.Floating>
                )}
              />
            </Col>
          </Row>
        ))}

      <Button
        style={{ height: '48px', marginTop: 'auto' }}
        variant="primary"
        type="submit"
        loading={loading}
        loadingPosition="start"
      >
        {t('save')}
      </Button>
    </Form>
  );
};

export default FillInInvoiceItem;
